import { useEffect, useRef } from 'react';

type ModalSize = 'md' | 'lg' | 'xl';

type Props = {
	open: boolean;
	onClose: () => void;
	children: React.ReactNode;
	size?: ModalSize;
	title?: string;
	/** Docks to the bottom edge as a full-width sheet on small screens. */
	sheet?: boolean;
};

const sizeClasses: Record<ModalSize, string> = {
	md: 'max-w-2xl max-h-[92vh]',
	lg: 'max-w-4xl max-h-[92vh]',
	xl: 'max-w-[85vw] max-h-[92vh]',
};

const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusable(container: HTMLElement): HTMLElement[] {
	return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
		(el) => el.offsetParent !== null,
	);
}

export function Modal({ open, onClose, children, size = 'md', title, sheet = false }: Props) {
	const dialogRef = useRef<HTMLDivElement>(null);
	const openerRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		if (!open) {
			return;
		}
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
				return;
			}
			if (e.key !== 'Tab') {
				return;
			}
			const focusable = dialogRef.current && getFocusable(dialogRef.current);
			if (!focusable || focusable.length === 0) {
				return;
			}
			const first = focusable[0]!;
			const last = focusable[focusable.length - 1]!;
			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		};
		document.addEventListener('keydown', handleKey);
		return () => document.removeEventListener('keydown', handleKey);
	}, [open, onClose]);

	useEffect(() => {
		if (!open) {
			return;
		}
		openerRef.current = document.activeElement as HTMLElement | null;
		const dialog = dialogRef.current;
		const initial = dialog?.querySelector<HTMLElement>('[data-autofocus]') ?? (dialog && getFocusable(dialog)[0]);
		initial?.focus();
		return () => {
			openerRef.current?.focus?.();
		};
	}, [open]);

	if (!open) {
		return null;
	}

	return (
		<div
			className={`fixed inset-0 z-[1200] flex justify-center bg-black/40 ${sheet ? 'items-end sm:items-center' : 'items-center'}`}
			onClick={onClose}
		>
			<div
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				{...(title ? { 'aria-label': title } : {})}
				className={`bg-white shadow-xl w-full overflow-y-auto ${sizeClasses[size]} ${sheet ? 'rounded-t-xl sm:rounded-xl sm:mx-4 max-sm:max-h-[100dvh]' : 'rounded-xl mx-4'}`}
				onClick={(e) => e.stopPropagation()}
			>
				{children}
			</div>
		</div>
	);
}
